import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  User,
  Users,
  Briefcase,
  Calendar,
  Bell,
  Network,
  LogOut,
  ChevronDown,
  Menu,
  X,
  MessageSquare,
  Bot,
  Settings,
  Trophy,
  MapPin,
  CreditCard,
  PlusCircle,
  Newspaper,
  ClipboardList,
  MessageCircle,
  ChevronRight, 
  Sparkles,
  Cpu,
  Star,
  Sun,Moon,Volume2,VolumeX
} from "lucide-react";
import CommandMenu from "./CommandMenu";
import { motion, AnimatePresence } from "framer-motion";
import MessageDrawer from "./MessageDrawer";
import SaarthiAI from "./SaarthiAI";

const SaarthiAIButton = ({ onClick }) => {
  return (
    <motion.button
      onClick={onClick}
      className="hidden lg:flex items-center px-5 py-2.5 rounded-xl
        relative group overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Primary Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-600" />
      
      {/* Animated Gradient Overlay */}
      <motion.div
        className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.2)_50%,transparent_75%)]"
        animate={{
          backgroundPosition: ["200% 200%", "-200% -200%"],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "linear",
        }}
        style={{
          backgroundSize: "250% 250%",
        }}
      />

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full"
            animate={{
              y: [-10, -30, -10],
              x: [i * 10, i * 15, i * 10],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: i * 0.4,
              ease: "easeInOut",
            }}
            style={{
              left: `${20 + i * 30}%`,
              top: "50%",
            }}
          />
        ))}
      </div>

      {/* Content Container */}
      <div className="relative flex items-center space-x-3">
        {/* Animated Bot Icon */}
        <motion.div
          className="relative"
          animate={{
            rotate: [0, 5, -5, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Cpu className="w-5 h-5 text-white" />
          {/* Glow Effect */}
          <motion.div
            className="absolute inset-0 bg-white/30 rounded-full blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </motion.div>

        {/* Text */}
        <span className="text-white font-semibold tracking-wide relative">
          Saarthi AI
          <motion.div
            className="absolute -right-4 -top-3"
            animate={{
              rotate: [0, 15, -15, 0],
              scale: [1, 1.2, 0.8, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <Star className="w-3 h-3 text-yellow-300 fill-yellow-300" />
          </motion.div>
        </span>

        {/* Right Icon */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Sparkles className="w-5 h-5 text-white" />
        </motion.div>
      </div>

      {/* Interactive Hover Effects */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-blue-400/0 via-white/20 to-purple-400/0"
        initial={{ x: "100%" }}
        whileHover={{ x: "-100%" }}
        transition={{ duration: 0.7 }}
      />

      {/* Border Glow */}
      <motion.div
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
          ring-2 ring-white/50 transition-opacity duration-300"
        animate={{
          scale: [1, 1.02, 1],
          opacity: [0, 0.5, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
    </motion.button>
  );
};


const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isCommandMenuOpen, setIsCommandMenuOpen] = useState(false);
  const [currentAlumni, setCurrentAlumni] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const profileDropdownRef = useRef(null);
  const [isChatDropdownOpen, setIsChatDropdownOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [isMessageDrawerOpen, setIsMessageDrawerOpen] = useState(false);
  const [isSaarthiAIOpen, setIsSaarthiAIOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [openMobileGroups, setOpenMobileGroups] = useState({});
  const [notifications, setNotifications] = useState([]);
const [isSpeaking, setIsSpeaking] = useState(false);
const [speechRate] = useState(0.5);

  const chatDropdownRef = useRef(null);

  useEffect(() => {
    const handleKeydown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "m") {
        e.preventDefault();
        setIsMessageDrawerOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  
  const speakContent = (text) => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = speechRate;
    utterance.pitch = 1;
    utterance.volume = 1;
    window.speechSynthesis.speak(utterance);
  };
  
  const toggleSpeech = () => {
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    } else {
      setIsSpeaking(true);
      const mainContent = document.querySelector('main')?.textContent || document.body.textContent;
      speakContent(mainContent);
    }
  };
  
  // Clean up speech synthesis when component unmounts
  useEffect(() => {
    return () => {
      if (window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 64); // Height of top navbar
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchCurrentAlumni = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/alumni/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch alumni details");
        const data = await response.json();
        setCurrentAlumni(data);
      } catch (err) {
        console.error("Error fetching alumni details:", err);
      }
    };

    fetchCurrentAlumni();
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        chatDropdownRef.current &&
        !chatDropdownRef.current.contains(event.target)
      ) {
        setIsChatDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    const fetchChats = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/messages/recent`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch chats");
        const data = await response.json();
        setChats(data);
      } catch (err) {
        console.error("Error fetching chats:", err);
      }
    };

    fetchChats();
    // Set up polling for new messages
    const interval = setInterval(fetchChats, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, []);
  const toggleMobileGroup = (groupName) => {
    setOpenMobileGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };
  const menuGroups = [
    {
      name: "Alumni",
      items: [
        { path: "/directory", label: "Alumni Directory", icon: Users },
        { path: "/alumni-map", label: "Alumni Map", icon: MapPin },
        { path: "/alma-card", label: "Alma Card", icon: CreditCard },
      ],
    },
    {
      name: "Careers",
      items: [
        { path: "/jobs", label: "Browse Jobs", icon: Briefcase },
        { path: "/jobs/create", label: "Post Job", icon: PlusCircle },
      ],
    },
    {
      name: "Events",
      items: [
        { path: "/events", label: "Browse Events", icon: Calendar },
        { path: "/events/create", label: "Host Event", icon: PlusCircle },
      ],
    },
    {
      name: "Donations",
      items: [
        { path: "/campaigns", label: "Campaigns", icon: Network },
        {
          path: "/campaigns/create",
          label: "Start Campaign",
          icon: PlusCircle,
        },
      ],
    },
    {
      name: "Updates",
      items: [
        { path: "/success-stories", label: "Success Stories", icon: Trophy },
        { path: "/newsletters", label: "Newsletters", icon: Newspaper },
        { path: "/survey", label: "Surveys", icon: ClipboardList },
        { path: "/feedback", label: "Feedback", icon: MessageCircle },
      ],
    },
  ];
  const mobileMenuItems = menuGroups.flatMap(group => 
    group.items.map(item => ({
      ...item,
      groupName: group.name
    }))
  );

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userRole");
    window.location.href = "/login";
  };
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    fetchNotification();
  }, []);


  const fetchNotification = async () => {
    try{
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/notifications`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if(!response.ok) throw new Error("Failed to fetch notifications");
      const data = await response.json();
      setNotifications(data);
      // console.log("notification ",data);
    }catch(err){
      console.error("Error fetching notifications:", err);
    }
  }

  const handleReadNotification = async (notificationId) => {
    try{
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/notifications/${notificationId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if(!response.ok) throw new Error("Failed to read notification");
      
    }catch(err){
      console.error("Error reading notification:", err);
    }finally{
      fetchNotification();
    }
  }

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div
        className={`transition-all duration-300 ${
          isScrolled
            ? "bg-white/80 backdrop-blur-lg border-b border-gray-100"
            : "bg-white"
        }`}
      >
        {/* Added px-6 for more horizontal space on sides */}
        <div className="max-w-[1440px] mx-auto px-0">  
          {/* Increased height to h-20 and added more gap between items */}
          <nav className="h-20 flex items-center justify-between gap-0">
            {/* Logo - Added more padding */}
            <Link to="/" className="flex-shrink-0 relative group pl-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <img
                  src="https://res.cloudinary.com/db4e3hqfv/image/upload/v1733974352/Saarthi_Logo_1_agutln.png"
                  alt="Saarthi"
                  className="h-20 w-auto" // Increased logo size
                />
                <div className="absolute inset-0 rounded-lg bg-blue-400/0 group-hover:bg-blue-400/10 transition-colors -m-2" />
              </motion.div>
            </Link>

            {/* Desktop Navigation - Added more spacing between items */}
            <div className="hidden lg:flex flex-1 items-center justify-center px-8">
              <div className="flex items-center space-x-1">
                {menuGroups.map((group) => (
                  <div key={group.name} className="relative group px-2">
                    <button className="px-2 py-2.5 rounded-xl text-gray-600 hover:text-blue-600 
                      transition-all duration-200 flex items-center space-x-2">
                      <span className="text-sm font-medium">{group.name}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>

                    {/* Dropdown Menu - Adjusted spacing */}
                    <div className="absolute top-full left-0 w-52 pt-2 opacity-0 invisible 
                      group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[9999]">
                      <div className="bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 
                        overflow-hidden p-1.5">
                        {group.items.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg text-sm 
                              transition-colors ${
                                location.pathname === item.path
                                  ? "bg-blue-50 text-blue-600"
                                  : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                              }`}
                          >
                            <item.icon className="w-4 h-4" />
                            <span>{item.label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Right Section */}
            <div className="flex items-center space-x-3">
              {/* Search Command */}
              <div className="hidden lg:flex items-center space-x-2">


    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={toggleSpeech}
      className="relative p-2 rounded-xl bg-gradient-to-b from-gray-50 to-gray-100 
        hover:from-gray-100 hover:to-gray-200 border border-gray-200/50 transition-all 
        duration-200"
      aria-label={isSpeaking ? "Stop speaking" : "Start speaking"}
    >
      {isSpeaking ? (
        <VolumeX className="w-5 h-5 text-gray-600" />
      ) : (
        <Volume2 className="w-5 h-5 text-gray-600" />
      )}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/0 
        to-blue-400/0 hover:from-blue-400/5 hover:to-purple-400/5 transition-colors" />
    </motion.button>
  </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsCommandMenuOpen(true)}
                className="hidden lg:flex items-center px-3 py-1.5 text-sm text-gray-500 
                  bg-gray-50/50 hover:bg-gray-100 border border-gray-200/50 rounded-lg 
                  transition-all duration-200 group"
              >
                <div className="flex items-center space-x-2 px-2">
                  <svg
                    className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <span className="text-gray-400 group-hover:text-gray-600 transition-colors">
                    Search...
                  </span>
                </div>
                <div
                  className="pl-2 px-1.5 py-0.5 text-xs text-gray-400 bg-gray-100 
                  rounded border border-gray-200 ml-2 group-hover:bg-gray-200 
                  transition-colors"
                >
                  ⌘K
                </div>
              </motion.button>

              {/* AI Assistant */}
            {/* <SaarthiAIButton onClick={() => setIsSaarthiAIOpen(true)} /> */}

              {/* Messages */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsMessageDrawerOpen(true)}
                className="relative p-2 rounded-xl bg-gradient-to-b from-gray-50 to-gray-100 
                  hover:from-gray-100 hover:to-gray-200 border border-gray-200/50 transition-all 
                  duration-200"
              >
                <MessageSquare className="w-5 h-5 text-gray-600" />
                {chats.some((chat) => chat.unread) && (
                  <span
                    className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full 
                    ring-2 ring-white animate-pulse"
                  />
                )}
                <div
                  className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/0 
                  to-blue-400/0 hover:from-blue-400/5 hover:to-purple-400/5 transition-colors"
                />
              </motion.button>

              {/* Notifications */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsNotificationOpen((prev) => !prev)}
                className="relative p-2 rounded-xl bg-gradient-to-b from-gray-50 to-gray-100 
                  hover:from-gray-100 hover:to-gray-200 border border-gray-200/50 transition-all 
                  duration-200"
              >
                <div className="relative">
                  <Bell className="w-5 h-5 text-gray-600" />
                  {notifications?.length > 0 && (
                    <span
                      className="absolute -top-2 -right-1 w-4 h-4 bg-red-500 text-white text-xs 
                      rounded-full flex items-center justify-center"
                    >
                      {notifications?.length}
                    </span>
                  )}
                </div>
                <div
                  className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/0 
                  to-blue-400/0 hover:from-blue-400/5 hover:to-purple-400/5 transition-colors"
                />
                
              </motion.button>
              {isNotificationOpen && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="absolute right-10 mt-2 w-200 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
                    style={{ top: '100%' }}
                  >
                    <div className="p-4 text-left">
                      <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
                      {notifications?.length === 0 ? (
                        <div className="mt-4 text-sm text-gray-500 w-200">No new notifications.</div>
                      ) : (
                        <ul className="mt-4 space-y-4 max-h-80 overflow-y-auto">
                          {notifications?.map((notification, index) => (
                            <li key={index} className="flex items-start space-x-4 border-b border-gray-200 pb-2">
                              <div className="flex-shrink-0">
                                <Bell className="w-6 h-6 text-blue-500" />
                              </div>
                              <div className="flex-1 text-left">
                                <p className="text-sm font-medium text-gray-900">{notification?.title}</p>
                                <p className="text-sm text-gray-500">{notification?.message}</p>
                                <p className="text-xs text-gray-400">{new Date(notification?.createdAt).toLocaleString()}</p>
                              </div>
                              <button
                                onClick={() => handleReadNotification(notification?._id)}
                                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </motion.div>
                )}
             

              {/* Profile Dropdown */}
              <div className="relative" ref={profileDropdownRef}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    setIsProfileDropdownOpen(!isProfileDropdownOpen)
                  }
                  className="flex items-center space-x-3 p-1.5 rounded-xl hover:bg-gray-50 
                    transition-all duration-200 group"
                >
                  {currentAlumni && (
                    <>
                      <div className="relative">
                        <img
                          src={
                            currentAlumni.personalInfo?.avatar ||
                            `https://ui-avatars.com/api/?name=${currentAlumni.firstName}+${currentAlumni.lastName}&background=random`
                          }
                          alt="Profile"
                          className="h-8 w-8 rounded-lg object-cover ring-2 ring-white"
                        />
                        <span
                          className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 
                          rounded-full ring-2 ring-white"
                        />
                      </div>
                      <div className="hidden lg:block text-left">
                        <p className="text-sm font-medium text-gray-900 line-clamp-1">
                          {currentAlumni.firstName} {currentAlumni.lastName}
                        </p>
                      </div>
                      <ChevronDown
                        className="w-4 h-4 text-gray-400 transition-transform 
                        duration-200 group-hover:text-gray-600"
                      />
                    </>
                  )}
                </motion.button>

                <AnimatePresence>
                  {isProfileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-72 origin-top-right bg-white 
                        rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden"
                    >
                      {/* Profile Section */}
                      <div className="relative px-4 py-4 bg-gradient-to-br from-blue-50 to-indigo-50">
                        <div className="flex items-start space-x-3">
                          <img
                            src={
                              currentAlumni.personalInfo?.avatar ||
                              `https://ui-avatars.com/api/?name=${currentAlumni.firstName}+${currentAlumni.lastName}&background=random`
                            }
                            alt="Profile"
                            className="h-12 w-12 rounded-lg object-cover ring-2 ring-white"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {currentAlumni.firstName} {currentAlumni.lastName}
                            </p>
                            <p className="text-sm text-gray-500 truncate">
                              {currentAlumni.email}
                            </p>
                            <div className="mt-2 flex items-center space-x-2">
                              <span
                                className="inline-flex items-center px-2 py-0.5 rounded-full 
                                text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                Verified Alumni
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                     {/* Menu Items */}
                     <div className="p-2 bg-white">
              {/* Mobile Navigation */}
              <div className="lg:hidden space-y-1">
                {menuGroups.map((group) => (
                  <div key={group.name} className="border-b border-gray-100 last:border-0">
                    <button
                      onClick={() => toggleMobileGroup(`profile-${group.name}`)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-900"
                    >
                      <span>{group.name}</span>
                      <ChevronRight
                        className={`w-4 h-4 text-gray-500 transition-transform duration-200 
                          ${openMobileGroups[`profile-${group.name}`] ? 'rotate-90' : ''}`}
                      />
                    </button>
                    <AnimatePresence>
                      {openMobileGroups[`profile-${group.name}`] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <div className="px-3 pb-2 space-y-1">
                            {group.items.map((item) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setIsProfileDropdownOpen(false)}
                                className={`
                                  flex items-center space-x-2 px-3 py-2 rounded-lg text-sm 
                                  transition-colors ${
                                    location.pathname === item.path
                                      ? "bg-blue-50 text-blue-600"
                                      : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                                  }
                                `}
                              >
                                {item.icon && <item.icon className="w-4 h-4" />}
                                <span>{item.label}</span>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>

                {/* Common Options */}
                <div className="space-y-1">
                  {/* Profile Link */}
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span>View Profile</span>
                  </Link>

                  {/* Settings */}
                  <Link
                    to="/profile/edit"
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Settings</span>
                  </Link>

                  {/* Mobile AI Assistant */}
                  <div className="lg:hidden">
                    <button
                      onClick={() => {
                        setIsSaarthiAIOpen(true);
                        setIsProfileDropdownOpen(false);
                      }}
                      className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      <Bot className="w-4 h-4" />
                      <span>AI Assistant</span>
                    </button>
                  </div>
                </div>

                {/* Logout */}
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
              </div>
      
          </nav>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
          >
            <div className="max-w-7xl mx-auto py-2 px-4 space-y-4">
              {menuGroups.map((group) => (
                <div key={group.name} className="space-y-1">
                  <div className="px-3 py-2 text-sm font-medium text-gray-900">
                    {group.name}
                  </div>
                  <div className="space-y-1">
                    {group.items.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`
                          flex items-center space-x-2 px-3 py-2 rounded-lg text-sm 
                          transition-colors ${
                            location.pathname === item.path
                              ? "bg-blue-50 text-blue-600"
                              : "text-gray-700 hover:bg-gray-50"
                          }
                        `}
                      >
                        {item.icon && <item.icon className="w-4 h-4" />}
                        <span>{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modals and Drawers */}
      <MessageDrawer
        isOpen={isMessageDrawerOpen}
        onClose={() => setIsMessageDrawerOpen(false)}
        chats={chats}
        onChatSelect={(chat) => {
          navigate(`/chat/${chat.connectionId}`);
          setIsMessageDrawerOpen(false);
        }}
      />
      <SaarthiAI isOpen={isSaarthiAIOpen} setIsOpen={setIsSaarthiAIOpen} />
      <CommandMenu
        isOpen={isCommandMenuOpen}
        setIsOpen={setIsCommandMenuOpen}
      />
    </header>
  );
};

export default Navbar;
